/**
 * Auth store — Zustand, NOT persisted.
 *
 * Access token lives in memory only (never localStorage/sessionStorage).
 * Refresh token rides an httpOnly+Secure+SameSite=Strict cookie managed
 * entirely by the browser — JavaScript cannot read or write it.
 *
 * Permissions drive what the UI offers. They are the authoritative `perms`
 * claim decoded from the access token (the backend resolves them from the
 * member's role). `role` is kept only for display. Server-side @PreAuthorize
 * is the real enforcement; these checks just hide affordances.
 */
import { create } from 'zustand';

/** Decode the `perms` claim from a JWT access token. Returns [] on any problem. */
function decodePermissions(token: string | null): string[] {
  if (!token) return [];
  try {
    const payload = token.split('.')[1];
    if (!payload) return [];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { perms?: unknown };
    return Array.isArray(claims.perms) ? (claims.perms as string[]) : [];
  } catch {
    return [];
  }
}

interface AuthState {
  /** In-memory only — NEVER persisted. */
  accessToken: string | null;
  role: string | null;
  permissions: string[];
  email: string | null;
  mustChangePassword: boolean;
  /**
   * The signed-in member's UUID id, for owner checks (e.g. "is this PERSONAL
   * dashboard mine?"). NOT decoded from the JWT — the token's `sub` claim is
   * the member's EMAIL (JwtIssuer.subject(email)), not an id. Sourced from
   * LoginResponse.user.id / MeResponse.id instead (see api/auth.ts).
   */
  memberId: string | null;

  /** memberId is an optional trailing arg (defaults to null) so existing
   *  call sites that only ever set the identity/permission fields keep
   *  compiling unchanged. */
  setLogin: (
    token: string,
    role: string | null,
    email: string | null,
    mustChange: boolean,
    memberId?: string | null,
  ) => void;
  clearAuth: () => void;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  accessToken: null,
  role: null,
  permissions: [],
  email: null,
  mustChangePassword: false,
  memberId: null,

  setLogin: (accessToken, role, email, mustChangePassword, memberId = null) =>
    set({ accessToken, role, email, mustChangePassword, memberId, permissions: decodePermissions(accessToken) }),

  clearAuth: () =>
    set({ accessToken: null, role: null, permissions: [], email: null, mustChangePassword: false, memberId: null }),

  hasRole: (role) => get().role === role,
  hasPermission: (permission) => get().permissions.includes(permission),
  hasAnyPermission: (...permissions) => permissions.some((p) => get().permissions.includes(p)),
}));
